import React, { useMemo } from 'react';
import { Project, SortField, SortOrder } from '../../types/Project';
import { ProjectCard } from '../molecules/ProjectCard';
import { sortProjects } from '../../utils/projectUtils';

interface ProjectListProps {
  projects: Project[];
  sortField: SortField;
  sortOrder: SortOrder;
}

const ProjectListHeader: React.FC<{
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
}> = ({ sortField, sortOrder, onSort }) => {
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg className="w-3.5 h-3.5 text-warm-500 dark:text-dark-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3.5 h-3.5 text-warm-500 dark:text-dark-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="hidden lg:grid bg-white/80 dark:bg-dark-300/80 backdrop-blur-sm rounded-3xl shadow-soft border-0 px-6 py-4 mb-3 grid-cols-[1fr_auto_80px_80px_100px_60px_60px_100px_140px_80px] gap-6 items-center">
      <button
        onClick={() => onSort('projectTitle')}
        className="flex items-center gap-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
      >
        Projeto
        <SortIcon field="projectTitle" />
      </button>

      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide text-center">
        Status
      </div>

      <button
        onClick={() => onSort('startDate')}
        className="flex items-center gap-1.5 justify-center text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
      >
        Início
        <SortIcon field="startDate" />
      </button>

      <button
        onClick={() => onSort('endDate')}
        className="flex items-center gap-1.5 justify-center text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
      >
        Fim
        <SortIcon field="endDate" />
      </button>

      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide text-center">
        Orçamento
      </div>

      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide text-center">
        Termo
      </div>

      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide text-center">
        Briefing
      </div>

      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide text-center">
        Creators
      </div>

      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide text-center">
        Progresso
      </div>

      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide text-center">
        Issues
      </div>
    </div>
  );
};

interface ProjectListFullProps extends ProjectListProps {
  onSort: (field: SortField) => void;
}

export const ProjectList: React.FC<ProjectListFullProps> = ({
  projects,
  sortField,
  sortOrder,
  onSort,
}) => {
  const sortedProjects = useMemo(() => {
    return sortProjects(projects, sortField, sortOrder);
  }, [projects, sortField, sortOrder]);

  if (sortedProjects.length === 0) {
    return (
      <div className="text-center py-20 bg-white/80 dark:bg-dark-300/80 backdrop-blur-sm rounded-3xl shadow-soft border-0">
        <p className="text-soft-gray dark:text-gray-400 text-lg font-medium">Nenhum projeto encontrado</p>
        <p className="text-soft-lightgray dark:text-gray-500 text-sm mt-2">Tente ajustar os filtros de busca</p>
      </div>
    );
  };

  return (
    <>
      <ProjectListHeader sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
      <div className="flex flex-col gap-3">
        {sortedProjects.map((project) => (
          <ProjectCard key={project.projectId} project={project} />
        ))}
      </div>
    </>
  );
};
